import { useState, useCallback, useEffect } from 'react';
import { MapPinIcon, WrenchIcon } from '@heroicons/react/24/outline';
import { EmployeeAccordionList } from '../EmployeeList/EmployeeAccordionList';
import { EquipmentAccordionList } from '../EquipmentList/EquipmentAccordionList';
import { ChevronDown } from 'react-feather';
import UserGroupIcon from '../../../../../public/assets/Icons-Users.svg';
import {
  getStatusLabel,
  getStatusBadgeClasses,
} from '@/shared/utils/statusBadge';
import { Snackbar } from '@/shared/components/Snackbar';
import { getFacility } from '../../api/viewFacilityApiService';
import type { ViewFacilityDetail } from '../../types/viewFacilityTypes';
import type { FacilityListItem } from '../../types/facilityListType';
import { FACILITY_LABELS } from '../../constants/facilityLabels';

interface FacilityAccordionListProps {
  facilities: FacilityListItem[];
  loading: boolean;
  error: string | null;
  clientId?: string;
  onMenuAction?:
    | ((action: string, facility: FacilityListItem) => void)
    | undefined;
  employeeIsValid?: boolean;
  onAccessDenied?: () => void;
}

interface FacilityAccordionCardProps {
  facility: FacilityListItem;
  expanded: boolean;
  onToggle: () => void;
  onClose: () => void;
  onDetailError: (msg: string) => void;
  index: number;
  clientId?: string;
  employeeIsValid?: boolean;
  onAccessDenied?: () => void;
}

// ---------- formatters ----------

const formatNip = (raw: string | null | undefined): string => {
  if (!raw) {
    return '—';
  }
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length === 0) {
    return '—';
  }
  if (digits.length > 8) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  }
  if (digits.length > 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return digits;
};

const formatPhone = (
  countryCode: string | null | undefined,
  phoneNumber: string | null | undefined
): string => {
  if (!phoneNumber) {
    return '—';
  }
  return countryCode ? `${countryCode} ${phoneNumber}` : phoneNumber;
};

// ---------- helpers ----------

const Field = ({
  label,
  value,
  breakWords = false,
  valueTestId,
}: {
  label: string;
  value: string | null | undefined;
  breakWords?: boolean;
  valueTestId?: string;
}) => (
  <div className="flex flex-col gap-[4px] items-start w-full">
    <p
      className="text-[#474A4C] w-full"
      style={{ fontSize: '12px', lineHeight: '16px', letterSpacing: '0.4px' }}
    >
      {label}
    </p>
    <p
      className={`text-[#181A1B] w-full${breakWords ? ' break-words' : ''}`}
      style={{ fontSize: '16px', lineHeight: '24px', letterSpacing: '0.5px' }}
      data-testid={valueTestId}
    >
      {value ?? '—'}
    </p>
  </div>
);

const SectionHeading = ({ title }: { title: string }) => (
  <p
    className="text-[#474A4C] font-normal w-full"
    style={{ fontSize: '18px', lineHeight: '26px' }}
  >
    {title}
  </p>
);

const ExpandedSkeleton = () => (
  <div className="flex flex-col gap-6 animate-pulse">
    <div className="flex flex-col gap-3">
      <div className="h-[26px] w-32 bg-[#ECEEEF] rounded" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="h-4 bg-[#ECEEEF] rounded w-24" />
            <div className="h-6 bg-[#ECEEEF] rounded w-32" />
          </div>
        ))}
      </div>
    </div>
    <div className="flex flex-col gap-3">
      <div className="h-[26px] w-32 bg-[#ECEEEF] rounded" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1">
            <div className="h-4 bg-[#ECEEEF] rounded w-24" />
            <div className="h-6 bg-[#ECEEEF] rounded w-32" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ---------- card ----------

type TabId = 'aparatura' | 'pracownicy';

const FacilityAccordionCard = ({
  facility,
  expanded,
  onToggle,
  onClose,
  onDetailError,
  index,
  clientId,
  employeeIsValid,
  onAccessDenied,
}: FacilityAccordionCardProps) => {
  const [detail, setDetail] = useState<ViewFacilityDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('aparatura');
  const [equipmentCount, setEquipmentCount] = useState(facility.equipmentCount);
  const [employeeCount, setEmployeeCount] = useState(
    facility.facilityUserCount
  );

  const handleTabChange = (tab: TabId) => setActiveTab(tab);

  useEffect(() => {
    if (!expanded) {
      setActiveTab('aparatura');
    }
  }, [expanded]);

  const badgeClasses = getStatusBadgeClasses(facility.status);
  const statusLabel = getStatusLabel(facility.status);

  const handleToggle = useCallback(async () => {
    const opening = !expanded;
    onToggle();
    if (opening && detail === null && !detailLoading) {
      setDetailLoading(true);
      const result = await getFacility(facility.id);
      if (result.success && result.data) {
        setDetail(result.data.facility);
      } else {
        onClose();
        onDetailError(result.error ?? 'Coś poszło nie tak. Spróbuj ponownie.');
      }
      setDetailLoading(false);
    }
  }, [
    expanded,
    detail,
    detailLoading,
    facility.id,
    onToggle,
    onClose,
    onDetailError,
  ]);

  return (
    <div className="border border-[#DCDDE0] rounded-xl overflow-hidden">
      {/* Main row */}
      <div
        className="px-4 py-4 flex items-center gap-4 cursor-pointer"
        onClick={() => void handleToggle()}
      >
        {/* Location icon */}
        <div className="shrink-0 text-[#474A4C] rounded-full bg-[#F2F4F5] p-2">
          <MapPinIcon className="w-6 h-6" color="#878B8E" />
        </div>

        {/* Name + meta */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <p
            className="text-bodyMedium text-[#181A1B] break-words min-w-0"
            data-testid={`facility-item-${index}`}
          >
            {facility.name} [{facility.facilityId}]
          </p>
          <div className="flex items-center flex-wrap gap-x-6 gap-y-1 text-[#474A4C]">
            <span className="flex items-center gap-1 text-bodySmall text-[#878B8E]">
              <WrenchIcon className="w-4 h-4" color="#878B8E" />
              {facility.equipmentCount} {FACILITY_LABELS.SECTION_EQUIPMENT}
            </span>
            <span className="flex items-center gap-1 text-bodySmall text-[#878B8E]">
              <UserGroupIcon width={16} height={16} />
              {employeeCount} {FACILITY_LABELS.SECTION_FACILITY_USER}
            </span>
            <span className="text-bodySmall text-[#878B8E]">
              {FACILITY_LABELS.CREATED_DATE_LABEL}:{' '}
              {new Date(facility.createdAt)
                .toLocaleDateString('pl-PL', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                })
                .replace(/\./g, '/')
                .split('/')
                .reverse()
                .join('/')}
            </span>
          </div>
        </div>

        {/* Badge + Chevron */}
        <div className="shrink-0 flex items-center gap-3">
          <span
            className={`shrink-0 px-3 py-1 rounded-[8px] text-sm font-medium flex items-center gap-2 ${badgeClasses.container}`}
            style={{ fontSize: '14px', lineHeight: '20px' }}
          >
            <div
              className={`rounded-full shrink-0 ${badgeClasses.dot}`}
              style={{ width: '6px', height: '6px' }}
            />
            {statusLabel}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              void handleToggle();
            }}
            className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#474A4C1A] active:bg-[#474A4C14] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] transition-transform duration-200"
            aria-label={`${expanded ? 'Zwiń' : 'Rozwiń'} szczegóły placówki ${facility.name}`}
            aria-expanded={expanded}
            data-testid={`facility-dropdown-${index}`}
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          >
            <ChevronDown size={24} strokeWidth={1.5} color="#474A4C" />
          </button>
        </div>
      </div>

      {/* Expanded section */}
      {expanded && (
        <div className="px-4 pt-2 pb-4 flex flex-col gap-4">
          {/* Facility + location details */}
          {detailLoading ? (
            <ExpandedSkeleton />
          ) : detail ? (
            <div className="flex flex-col gap-6">
              {/* Dane placówki */}
              <div className="flex flex-col gap-3">
                <SectionHeading
                  title={FACILITY_LABELS.FACILITY_DETAILS_TITLE}
                />
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-3">
                  <Field
                    label={FACILITY_LABELS.FIELD_PHONE}
                    value={formatPhone(detail.countryCode, detail.phoneNumber)}
                    valueTestId={`facility-value-phone-${index}`}
                  />
                  <Field
                    label={FACILITY_LABELS.FIELD_NIP}
                    value={formatNip(detail.nip)}
                    valueTestId={`facility-value-nip-${index}`}
                  />
                </div>
              </div>

              {/* Dane adresowe */}
              <div className="flex flex-col gap-3">
                <SectionHeading title={FACILITY_LABELS.SECTION_ADDRESS} />
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-3">
                  <Field
                    label={FACILITY_LABELS.FIELD_ADDRESS1}
                    value={detail.addressLine1}
                    breakWords
                    valueTestId={`facility-value-address1-${index}`}
                  />
                  <Field
                    label={FACILITY_LABELS.FIELD_ADDRESS2}
                    value={detail.addressLine2}
                    breakWords
                    valueTestId={`facility-value-address2-${index}`}
                  />
                  <Field
                    label={FACILITY_LABELS.FIELD_CITY}
                    value={detail.city}
                    breakWords
                    valueTestId={`facility-value-city-${index}`}
                  />
                  <Field
                    label={FACILITY_LABELS.FIELD_STATE}
                    value={detail.state}
                    breakWords
                    valueTestId={`facility-value-state-${index}`}
                  />
                  <Field
                    label={FACILITY_LABELS.FIELD_ZIP}
                    value={detail.zipCode}
                    breakWords
                    valueTestId={`facility-value-zip-${index}`}
                  />
                  <Field
                    label={FACILITY_LABELS.FIELD_COUNTRY}
                    value={detail.country}
                    breakWords
                    valueTestId={`facility-value-country-${index}`}
                  />
                </div>
              </div>
            </div>
          ) : null}

          {/* Tab toggle + list shimmer */}
          {detailLoading && (
            <div className="flex flex-col gap-4 animate-pulse">
              <div className="h-[48px] w-full max-w-[432px] rounded-[12px] bg-[#ECEEEF]" />
              <div className="border border-[#DCDDE0] rounded-2xl px-4 py-3 h-[68px] bg-[#F5F5F5]" />
            </div>
          )}

          {/* Tab toggle */}
          {!detailLoading && (
            <div className="flex bg-[#ECEEEF] rounded-[12px] p-1 w-full max-w-[432px] h-[48px]">
              {(
                [
                  {
                    id: 'aparatura',
                    label: `${FACILITY_LABELS.SECTION_EQUIPMENT} (${equipmentCount})`,
                  },
                  {
                    id: 'pracownicy',
                    label: `Pracownicy (${employeeCount})`,
                  },
                ] as { id: TabId; label: string }[]
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  data-testid={`facility-view-tab-${tab.id}`}
                  onClick={() => handleTabChange(tab.id)}
                  className={[
                    'flex-1 h-[40px] py-[8px] rounded-[8px] text-[16px] leading-[24px] tracking-[0.15px] font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] cursor-pointer',
                    activeTab === tab.id
                      ? 'bg-white text-[#2A4455] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.15),0px_2px_6px_2px_rgba(0,0,0,0.08)]'
                      : 'bg-transparent text-[#878B8E]',
                  ].join(' ')}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Tab content — both lists are always mounted when the card is open
              so they fetch and report the real counts immediately on expansion,
              regardless of which tab is active. */}
          {clientId && (
            <div hidden={detailLoading || activeTab !== 'aparatura'}>
              <EquipmentAccordionList
                facilityId={facility.id}
                clientId={clientId}
                onTotalCountChange={setEquipmentCount}
                {...(onAccessDenied !== undefined && { onAccessDenied })}
              />
            </div>
          )}
          <div hidden={detailLoading || activeTab !== 'pracownicy'}>
            <EmployeeAccordionList
              facilityId={facility.id}
              {...(employeeIsValid !== undefined && {
                isValid: employeeIsValid,
              })}
              onTotalCountChange={setEmployeeCount}
              {...(onAccessDenied !== undefined && { onAccessDenied })}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// ---------- list ----------

export const FacilityAccordionList = ({
  facilities,
  loading,
  error,
  clientId,
  employeeIsValid,
  onAccessDenied,
}: FacilityAccordionListProps) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const showError = useCallback((msg: string) => {
    setSnackbarMessage(msg);
    setSnackbarOpen(true);
  }, []);

  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error, showError]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 1 }).map((_, i) => (
              <div
                key={i}
                className="border border-[#DCDDE0] rounded-2xl px-4 py-3 h-[68px] animate-pulse bg-[#F5F5F5]"
              />
            ))}
          </div>
        ) : facilities.length === 0 && !error ? (
          <p className="text-bodyLarge text-[#474A4C]">
            Brak placówek do wyświetlenia.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {facilities.map((facility, index) => (
              <FacilityAccordionCard
                key={facility.id}
                facility={facility}
                index={index}
                expanded={expandedId === facility.id}
                onToggle={() =>
                  setExpandedId((prev) =>
                    prev === facility.id ? null : facility.id
                  )
                }
                onClose={() => setExpandedId(null)}
                onDetailError={showError}
                {...(clientId !== undefined && { clientId })}
                {...(employeeIsValid !== undefined && { employeeIsValid })}
                {...(onAccessDenied !== undefined && { onAccessDenied })}
              />
            ))}
          </div>
        )}
      </div>

      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        variant="error"
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton
      />
    </div>
  );
};
